import { useMemo, useSyncExternalStore } from 'react'
import type { DdmStream } from '@bloo-team/api-client'
import { isEntityKind, type EntityKind } from '@bloo-team/core-shell'

/**
 * Data Model overlay — localStorage v1 (docs/data-model-requirements.md
 * §Entity-kind overlay, §Demo mode).
 *
 * Two things live here, both draft API contracts:
 *
 *   `tags["<stream>/<field>"]`  the entity kind a field carries. DDM stores
 *                               bare names only; the kind is console-side
 *                               metadata until CB-35 puts it on the row.
 *                               Read in BOTH modes.
 *   `added: DdmStream[]`        demo-only echo of `POST /api/ddm` (D1): a
 *                               new stream row, or the fixture row's copy
 *                               with the added field. Live mode never
 *                               writes it — the backend is the state.
 *
 * `seedTags(rows)` derives a starting set from entity search's role fields
 * (`KIND_FIELDS`, the map the requirements pin) so the page is not empty on
 * first open; `effectiveTags` lays the explicit tags over the seeds. Seeds
 * are never persisted — only what a person set is.
 *
 * ONE key per cluster+scope, `bloo.data-model.<clusterId>.<scopeId>`. House
 * localStorage rules (cf. storage-store / extractor-store): try/catch
 * everywhere, SSR-safe, a corrupt blob is superseded by the next write.
 */

export interface DataModelOverlay {
  tags: Record<string, EntityKind>
  added: DdmStream[]
}

export const DATA_MODEL_KEY_PREFIX = 'bloo.data-model.'

export function dataModelOverlayKey(clusterId: string, scopeId: string): string {
  return `${DATA_MODEL_KEY_PREFIX}${clusterId}.${scopeId}`
}

/** The overlay's tag key for a field. */
export function fieldKey(stream: string, field: string): string {
  return `${stream}/${field}`
}

/**
 * Role field → kind, from the requirements' seed map (a subset of entity
 * search's `KIND_FIELDS`, restricted to the unambiguous roles).
 */
export const SEED_KINDS: Readonly<Record<string, EntityKind>> = Object.freeze({
  SrcIP: 'ip', DstIP: 'ip', DevSrcIP: 'ip',
  User: 'user', TargetUser: 'user',
  System: 'host', SrcHost: 'host', DstHost: 'host',
  Hash: 'hash',
  URL: 'url',
  Domain: 'domain',
  File: 'file', FilePath: 'file',
  Action: 'action',
})

/** Seed tags for the role fields present in `rows` — nothing for fields the model does not declare. */
export function seedTags(rows: DdmStream[]): Record<string, EntityKind> {
  const out: Record<string, EntityKind> = {}
  for (const r of rows) {
    for (const f of r.fields) {
      const kind = SEED_KINDS[f]
      if (kind) out[fieldKey(r.stream, f)] = kind
    }
  }
  return out
}

/**
 * Seeds, overridden by the explicit tags. Accepts the overlay, the
 * `useDataModelOverlay` view, or just a `tags` map (field keys always carry a
 * `/`, so a bare map can never be mistaken for the wrapper).
 */
export function effectiveTags(
  rows: DdmStream[],
  overlay: DataModelOverlay | DataModelOverlayView | Record<string, EntityKind>,
): Record<string, EntityKind> {
  const wrapped = 'tags' in overlay && overlay.tags !== null && typeof overlay.tags === 'object'
  const explicit = wrapped ? (overlay as DataModelOverlay).tags : (overlay as Record<string, EntityKind>)
  return { ...seedTags(rows), ...explicit }
}

const EMPTY: DataModelOverlay = Object.freeze({
  tags: Object.freeze({}) as Record<string, EntityKind>,
  added: Object.freeze([]) as unknown as DdmStream[],
}) as DataModelOverlay

const strOrUndef = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)
const strOrNull = (v: unknown): string | null | undefined => (v === null ? null : strOrUndef(v))

/** A `DdmStream` from storage, or undefined when it is not one. */
function readRow(v: unknown): DdmStream | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  if (typeof o.stream !== 'string' || o.stream.length === 0) return undefined
  const fields = Array.isArray(o.fields) ? o.fields.filter((f): f is string => typeof f === 'string') : []
  const row: DdmStream = { stream: o.stream, fields }
  const id = strOrUndef(o.stream_id)
  if (id !== undefined) row.stream_id = id
  const scope = strOrUndef(o.scope_id)
  if (scope !== undefined) row.scope_id = scope
  for (const k of ['created_by', 'updated_by', 'create_tstamp', 'update_tstamp'] as const) {
    const s = strOrNull(o[k])
    if (s !== undefined) row[k] = s
  }
  return row
}

/** Parse a stored blob defensively: unknown keys dropped, bad entries skipped. */
export function readDataModelOverlay(parsed: unknown): DataModelOverlay {
  const out: DataModelOverlay = { tags: {}, added: [] }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return out
  const root = parsed as Record<string, unknown>
  const tags = root.tags
  if (tags && typeof tags === 'object' && !Array.isArray(tags)) {
    for (const [key, kind] of Object.entries(tags as Record<string, unknown>)) {
      if (key.includes('/') && isEntityKind(kind)) out.tags[key] = kind
    }
  }
  if (Array.isArray(root.added)) {
    const seen = new Set<string>()
    for (const v of root.added) {
      const row = readRow(v)
      if (!row || seen.has(row.stream)) continue
      seen.add(row.stream)
      out.added.push(row)
    }
  }
  return out
}

export interface DataModelOverlayStore {
  /** Stable snapshot until the next mutation. */
  all: () => DataModelOverlay
  /** Tag a field (`fieldKey(stream, field)`) with a kind. */
  setTag: (key: string, kind: EntityKind) => void
  /** Drop an explicit tag; the seed, if any, shows through again. */
  clearTag: (key: string) => void
  /** Echo of `POST /api/ddm`: upsert by stream name — a new row, or a fixture row's widened copy. */
  addRow: (row: DdmStream) => void
  /** Drop every override — demo back to fixture state, tags included. */
  reset: () => void
  subscribe: (cb: () => void) => () => void
}

class LocalStorageDataModelStore implements DataModelOverlayStore {
  private cache: DataModelOverlay | null = null
  private listeners = new Set<() => void>()
  constructor(private readonly key: string) {}

  all = (): DataModelOverlay => this.read()

  setTag = (key: string, kind: EntityKind) => {
    const cur = this.read()
    if (cur.tags[key] === kind) return
    this.write({ ...cur, tags: { ...cur.tags, [key]: kind } })
  }

  clearTag = (key: string) => {
    const cur = this.read()
    if (!(key in cur.tags)) return
    const { [key]: _gone, ...tags } = cur.tags
    this.write({ ...cur, tags })
  }

  addRow = (row: DdmStream) => {
    const cur = this.read()
    const added = cur.added.some((r) => r.stream === row.stream)
      ? cur.added.map((r) => (r.stream === row.stream ? row : r))
      : [...cur.added, row]
    this.write({ ...cur, added })
  }

  reset = () => {
    try {
      globalThis.localStorage?.removeItem(this.key)
    } catch {
      /* no storage: nothing to drop */
    }
    this.cache = EMPTY
    this.notify()
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => {
      this.listeners.delete(cb)
    }
  }

  private read(): DataModelOverlay {
    if (this.cache) return this.cache
    let parsed: unknown
    try {
      const raw = globalThis.localStorage?.getItem(this.key)
      parsed = raw ? JSON.parse(raw) : {}
    } catch {
      parsed = {}
    }
    this.cache = readDataModelOverlay(parsed)
    return this.cache
  }

  private write(next: DataModelOverlay) {
    try {
      globalThis.localStorage?.setItem(this.key, JSON.stringify(next))
    } catch {
      /* quota or no storage: keep the in-memory overlay so the UI still reflects the action */
    }
    this.cache = next
    this.notify()
  }

  private notify() {
    for (const cb of this.listeners) cb()
  }
}

const stores = new Map<string, DataModelOverlayStore>()

/** One store per cluster+scope, shared by every hook and mutation on the page. */
export function dataModelStore(clusterId: string, scopeId: string): DataModelOverlayStore {
  const key = dataModelOverlayKey(clusterId, scopeId)
  let s = stores.get(key)
  if (!s) {
    s = new LocalStorageDataModelStore(key)
    stores.set(key, s)
  }
  return s
}

/** Live snapshot of the raw overlay for one scope (what `useDataModel` applies). */
export function useDataModelOverlayState(clusterId: string, scopeId: string): DataModelOverlay {
  const store = dataModelStore(clusterId, scopeId)
  return useSyncExternalStore(store.subscribe, store.all, store.all)
}

export interface DataModelOverlayView {
  /** EXPLICIT tags only — lay them over `seedTags(rows)` with `effectiveTags` for display. */
  tags: Record<string, EntityKind>
  /** Demo-added rows (empty in live mode). */
  added: DdmStream[]
  setTag: (key: string, kind: EntityKind) => void
  clearTag: (key: string) => void
}

/** Live view of the overlay plus its tag writes — what the page binds the kind chips to. */
export function useDataModelOverlay(clusterId: string, scopeId: string): DataModelOverlayView {
  const store = dataModelStore(clusterId, scopeId)
  const state = useDataModelOverlayState(clusterId, scopeId)
  return useMemo(
    () => ({ tags: state.tags, added: state.added, setTag: store.setTag, clearTag: store.clearTag }),
    [state, store],
  )
}

/** Demo seeder body: forget every data-model overlay in this browser. Idempotent. */
export function resetAllDataModelOverlays(): void {
  const ls = globalThis.localStorage
  if (ls) {
    try {
      for (let i = ls.length - 1; i >= 0; i--) {
        const key = ls.key(i)
        if (key && key.startsWith(DATA_MODEL_KEY_PREFIX)) ls.removeItem(key)
      }
    } catch {
      /* no storage */
    }
  }
  for (const s of stores.values()) s.reset()
}
